import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { FaDownload, FaPauseCircle, FaPlayCircle } from "react-icons/fa";
import config from "../../config";
import { useLanguage } from "../../Context";
import { MdAudiotrack } from "react-icons/md";

export default function AudioPreview() {
  const { subcategory, id } = useParams();
  const [audioData, setAudioData] = useState(null);
  const [relatedContents, setRelatedContents] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const audioRef = useRef(null);

  const { language } = useLanguage();

  // Fetch audio data
  useEffect(() => {
    const fetchAudioData = async () => {
      try {
        const response = await fetch(
          `${config.apiUrl}contentapi.php?option=1&type=6&subcategory=${subcategory}&id=${id}`
        );
        const data = await response.json();
        setAudioData(data[0]); // Assuming the API returns an array
      } catch (error) {
        console.error("Error fetching audio data:", error);
      }
    };

    fetchAudioData();
  }, [subcategory, id]);

  useEffect(() => {
    fetch(
      `${config.apiUrl}contentapi.php?option=1&type=7&subcategory=${subcategory}&limitCount=4&id=${id}&lang=${language}`
    )
      .then((response) => response.json())
      .then((relatedContents) => setRelatedContents(relatedContents))
      .catch((error) => console.error("Error fetching data", error));
  }, [subcategory, id]);

  // Handle play/pause toggle
  const toggleAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Handle seek bar change
  const handleSeek = (e) => {
    const newTime = parseFloat(e.target.value);
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  // Update current time during playback
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Set audio duration when metadata is loaded
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  if (!audioData) {
    return <div>Loading...</div>;
  }

  const styles = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(16rem, 2fr))",
    gap: "1em",
  };

  const relatedAudios = relatedContents.map((audio) => (
    <div key={audio.id} className="card bg-body-secondary border-0 rounded-1">
      <Link
        to={`/audio/${audio.SubCategory}/${audio.id}`}
        className="icon-links blue-links text-body"
      >
        <div className="ratio ratio-16x9">
          <MdAudiotrack
            className="position-absolute m-2"
            alt=""
            style={{
              width: "2.5rem",
              height: "2.5rem",
              color: "white",
              border: "0.2rem solid white",
              borderRadius: "50%",
              padding: "0.3rem",
              zIndex: "10",
              right: "0",
            }}
          />
          <img
            className="w-100 h-100 object-fit-cover"
            src={audio.ImageUrl}
            alt="audio thumbnails"
          />
        </div>
        <p className="fs-6 m-0 text-center p-3">{audio.Title}</p>
      </Link>
    </div>
  ));

  return (
    <div className="bg-body">
      <div className="max-container-width py-3 text-body">
        <div
          className="card mx-auto border-0 bg-body-tertiary"
          style={{ maxWidth: "48rem" }}
        >
          <img
            src={audioData.ImageUrl}
            className="card-img-top object-fit-cover"
            alt={audioData.Title}
            style={{ aspectRatio: "16/9" }}
          />
          <audio
            ref={audioRef}
            src={audioData.audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
          />
          <div className="card-body px-3 pb-2">
            <p className="card-text primary-text-blue mb-1">
              {audioData.Title}
            </p>
            <input
              dir="ltr"
              type="range"
              className="custom-range"
              min={0}
              max={audioDuration || 0}
              step={1}
              value={currentTime}
              onChange={handleSeek}
            />
            <div style={{ textAlign: "end" }}>
              {formatTime(currentTime)} / {formatTime(audioDuration)}
            </div>
          </div>
          <a
            href={audioData.audioUrl}
            target={"_blank"}
            rel={"noreferrer"}
            style={{
              position: "absolute",
              bottom: "1.5em",
              right: "1em",
            }}
            className="icon-links link-dark text-decoration-none"
          >
            <FaDownload style={{ width: "1.5em", height: "1.5em" }} />
          </a>
          <button className="btn pb-3" onClick={toggleAudio}>
            {isPlaying ? (
              <FaPauseCircle style={{ width: "2em", height: "2em" }} />
            ) : (
              <FaPlayCircle style={{ width: "2em", height: "2em" }} />
            )}
          </button>
        </div>
        <p className="text-center">{audioData.PostDate}</p>
        <div className="text-center col-8 mx-auto">{audioData.ShortDesc}</div>
        <div className="p-4">
          <h4>
            {parseInt(language) === 1 ? "موضوعات مرتبط" : "اړوند موضوعات"}
          </h4>
          <div style={styles}>{relatedAudios}</div>
        </div>
      </div>
    </div>
  );
}
